'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { useForm } from 'react-hook-form';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useTransition } from 'react';

const AVAILABLE_TOPICS = [
  'Next JS',
  'Medusa JS',
  'Directus',
  'UI/UX Development',
  'Jamstack Development',
  'Headless Commerce Development',
  'MVP & POC Development',
  'Custom Software Development',
  'Product Design Services',
  'CTO as a Service',
  'API Development',
];

const ExplorePopupForm = ({ setShowPopup }) => {
  const [isPending, startTransition] = useTransition();
  const [selectedTopics, setSelectedTopics] = useState([]);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({});

  const onSubmit = async (data) => {
    startTransition(async () => {
      try {
        const formSubmitResponse = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/items/contacted_users`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              name: data?.fullName,
              email: data?.email,
              description: '',
              services_selected: selectedTopics,
            }),
          }
        );

        if (!formSubmitResponse.ok) {
          toast.error(
            'Your message could not be sent. Please try again later.'
          );
        }

        toast.success(
          'Your message has been sent to the team. They will get back to you soon'
        );
        reset();
        setShowPopup(false);
      } catch (err) {
        console.log(err);
        toast.error('Your message could not be sent. Please try again later.');
      }
    });
  };

  const addTopic = (topic) => {
    if (!selectedTopics.some((t) => t.name === topic.name)) {
      setSelectedTopics([...selectedTopics, { name: topic }]);
    }
  };

  const removeTopic = (topic) => {
    const updatedTopics = selectedTopics.filter((t) => t.name !== topic.name);
    setSelectedTopics(updatedTopics);
  };

  return (
    <form className="mt-10" onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-3 md:space-y-4 lg:space-y-6">
        <div className="space-y-1.5 md:space-y-2 lg:space-y-2.5">
          <div className="flex flex-wrap justify-between items-center gap-1.5">
            <label
              htmlFor="fullName"
              className="md:text-lg text-base lg:text-xl"
            >
              Full Name<span className="text-[#F63030]">*</span>
            </label>
            {errors?.fullName && (
              <p className="text-xs text-red-400 mt-1">
                {errors?.fullName?.message}
              </p>
            )}
          </div>
          <div className="relative">
            <input
              {...register('fullName', {
                required: 'FullName is required!',
                minLength: {
                  value: 3,
                  message: 'FullName is too short!',
                },
              })}
              type="text"
              placeholder="Enter your name"
              id="fullName"
              disabled={isPending}
              className={cn(
                'w-full bg-[#020c0d] font-light rounded-lg border border-gray-300 border-opacity-25 md:text-lg text-base lg:text-xl md:placeholder:lg placeholder:text-base lg:placeholder:text-xl py-3 outline-none text-gray-200 px-4 leading-8 transition-colors placeholder:text-gray-300/50 duration-200 ease-in-out',
                isPending && 'cursor-not-allowed opacity-50'
              )}
            />
            <div className="absolute top-3.5 right-4">
              <div className="h-7 w-7 relative">
                <Image
                  src="/icons/lock.svg"
                  fill
                  className="object-cover absolute"
                  alt="Lock Icon"
                />
              </div>
            </div>
          </div>
          <span className="inline-block text-xs md:text-sm text-[#595959]">
            This is your public display name, it can be your real name or your
            nickname. Your message will be submitted under this name.
          </span>
        </div>
        <div className="space-y-1.5 md:space-y-2 lg:space-y-2.5">
          <div className="flex items-center justify-between gap-1.5 flex-wrap">
            <label htmlFor="email" className="md:text-lg text-base lg:text-xl">
              Email ID<span className="text-[#F63030]">*</span>
            </label>
            {errors?.email && (
              <p className="text-xs text-red-400 mt-1">
                {errors?.email?.message}
              </p>
            )}
          </div>
          <div className="relative">
            <input
              {...register('email', {
                required: 'Email is required!',
                pattern: {
                  value: /^\S+@\S+\.\S+$/,
                  message: 'Invalid email!',
                },
              })}
              disabled={isPending}
              type="email"
              placeholder="Enter your email address"
              id="email"
              className={cn(
                'w-full  bg-[#020c0d]  font-light rounded-lg border border-gray-300 border-opacity-25 md:text-lg text-base lg:text-xl md:placeholder:lg placeholder:text-base lg:placeholder:text-xl py-3 outline-none text-gray-200 px-4 leading-8 transition-colors placeholder:text-gray-300/50 duration-200 ease-in-out',
                isPending && 'cursor-not-allowed opacity-50'
              )}
            />
            <div className="absolute top-3.5 right-4">
              <div className="h-7 w-7 relative">
                <Image
                  src="/icons/lock.svg"
                  fill
                  className="object-cover absolute"
                  alt="Lock Icon"
                />
              </div>
            </div>
          </div>
          <span className="inline-block text-xs md:text-sm text-[#595959]">
            This is your public display email. Your message will be submitted
            under this email.
          </span>
        </div>
        <div className="space-y-1.5 md:space-y-2 lg:space-y-2.5">
          <div className="flex items-center justify-between gap-1.5 flex-wrap">
            <label htmlFor="topic" className="md:text-lg text-base lg:text-xl">
              Select Topics<span className="text-[#F63030]">*</span>
            </label>
            {errors.topics && (
              <p className="text-xs text-red-400">{errors.topics.message}</p>
            )}
          </div>
          <div className="relative">
            <select
              onChange={(e) => {
                const selectedTopic = e.target.value;
                if (selectedTopic) {
                  addTopic(selectedTopic);
                  e.target.value = '';
                }
              }}
              disabled={
                selectedTopics.length === AVAILABLE_TOPICS.length || isPending
              }
              className="w-full bg-[#020c0d] font-light rounded-lg border border-gray-300 border-opacity-25 md:text-lg text-base lg:text-xl  placeholder:text-xl py-3 outline-none text-gray-300/50 px-4 leading-8 transition-colors duration-200 appearance-none ease-in-out pr-10 invalid:text-gray-300/50"
            >
              <option value="" hidden>
                Choose topics...
              </option>
              {AVAILABLE_TOPICS.filter(
                (topic) =>
                  !selectedTopics.some((selected) => selected.name === topic)
              ).map((topic) => (
                <option key={topic} className="text-white" value={topic}>
                  {topic}
                </option>
              ))}
            </select>

            <div className="absolute inset-y-0 right-0 flex items-center px-4">
              <ChevronDown />
            </div>
          </div>
          <div>
            {selectedTopics?.length > 0 && (
              <div className="flex flex-wrap gap-3">
                {selectedTopics?.map((topic) => (
                  <div
                    key={topic}
                    className="flex borderGrd font-bold !border-[1px] text-gray-200 items-center px-3  rounded-full text-xs cursor-pointer"
                  >
                    {topic?.name}
                    <button
                      type="button"
                      disabled={isPending}
                      onClick={() => removeTopic(topic)}
                      className="ml-2 text-lg text-gray-400 hover:text-gray-100"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <button
        type="submit"
        disabled={isPending}
        className="border transition-all mt-10 duration-300 hover:text-[#020c0d] hover:bg-white border-[#E9F1F2] w-full rounded-xl font-bold text-[18px] capitalize py-4 "
      >
        {isPending ? 'Submitting...' : 'Submit your request'}
      </button>
    </form>
  );
};

export default ExplorePopupForm;
